// =============================================================================
// useDocFavorites - Shared Zustand store for document favorites
// =============================================================================

import { create } from 'zustand';
import {
  fetchDocFavorites,
  addDocFavorite,
  removeDocFavorite,
  type DocFavorite,
} from '../data/documentFavoriteRepository';
import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';

interface DocFavoritesState {
  favorites: DocFavorite[];
  loading: boolean;
  _userId: string | null;
  _orgId: string | null;
  load: (userId: string, orgId: string) => Promise<void>;
  toggle: (userId: string, orgId: string, documentId: string, title?: string, icon?: string, isWiki?: boolean) => Promise<void>;
  isFavorite: (documentId: string) => boolean;
}

const useDocFavoritesStore = create<DocFavoritesState>((set, get) => ({
  favorites: [],
  loading: false,
  _userId: null,
  _orgId: null,

  load: async (userId, orgId) => {
    set({ loading: true, _userId: userId, _orgId: orgId });
    try {
      const data = await fetchDocFavorites(userId, orgId);
      set({ favorites: data, loading: false });
    } catch (err) {
      console.error('Error loading doc favorites:', err);
      set({ loading: false });
    }
  },

  isFavorite: (documentId) => {
    return get().favorites.some(f => f.document_id === documentId);
  },

  toggle: async (userId, orgId, documentId, title, icon, isWiki) => {
    const { favorites } = get();
    const existing = favorites.find(f => f.document_id === documentId);

    if (existing) {
      // Optimistic remove
      set({ favorites: favorites.filter(f => f.document_id !== documentId) });
      const ok = await removeDocFavorite(userId, documentId);
      if (!ok) {
        // Rollback
        const data = await fetchDocFavorites(userId, orgId);
        set({ favorites: data });
      }
    } else {
      // Optimistic add
      const temp: DocFavorite = {
        id: `temp-${Date.now()}`,
        document_id: documentId,
        document_title: title || 'Sans titre',
        document_icon: icon || (isWiki ? '📖' : '📄'),
        is_wiki: isWiki || false,
      };
      set({ favorites: [...favorites, temp] });
      const result = await addDocFavorite(userId, orgId, documentId);
      if (result) {
        set(s => ({
          favorites: s.favorites.map(f => (f.id === temp.id ? { ...f, id: result.id } : f)),
        }));
      } else {
        set(s => ({ favorites: s.favorites.filter(f => f.id !== temp.id) }));
      }
    }
  },
}));

/** Hook wrapper that auto-loads favorites for current user/org */
export function useDocFavorites() {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const store = useDocFavoritesStore();

  useEffect(() => {
    if (user?.id && currentOrganization?.id) {
      // Only reload if user/org changed
      if (store._userId !== user.id || store._orgId !== currentOrganization.id) {
        store.load(user.id, currentOrganization.id);
      }
    }
  }, [user?.id, currentOrganization?.id]);

  const toggleFavorite = (documentId: string, title?: string, icon?: string, isWiki?: boolean) => {
    if (!user?.id || !currentOrganization?.id) return;
    store.toggle(user.id, currentOrganization.id, documentId, title, icon, isWiki);
  };

  return {
    favorites: store.favorites,
    docFavorites: store.favorites.filter(f => !f.is_wiki),
    wikiFavorites: store.favorites.filter(f => f.is_wiki),
    loading: store.loading,
    isFavorite: store.isFavorite,
    toggleFavorite,
    refresh: () => {
      if (user?.id && currentOrganization?.id) {
        store.load(user.id, currentOrganization.id);
      }
    },
  };
}
